import React, { useId, useState } from 'react';
import { Alert, Collapse } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import XpPotionBonusResultLine from './XpPotionBonusResultLine';

/** Mirrors solo quiz results XP breakdown; PvP adds arena multiplier and winner bonus lines. */
export interface PvpResultXpBreakdownParticipant {
  earnedXP: number;
  baseXp: number;
  bonusXpPerCorrectAnswer: number;
  totalBonusXp: number;
  correctAnswers: number;
  soloVipXpBonusAmount: number;
  pvpArenaExtraXp: number;
  winnerBonusXp: number;
  /** Same semantics as solo quiz response; omit to hide the speed row. */
  speedBonusXp?: number | null;
  /** PWA app bonus (server); omit or 0 to hide. */
  pvpAppXpBonusAmount?: number;
  pvpAppXpBonusPercent?: number | null;
  /** Inventory XP potion extra (solo-equivalent fields from server). */
  soloXpPotionBonusAmount?: number;
  soloXpPotionBonusPercent?: number | null;
}

const PvpResultParticipantXpBlock: React.FC<{ participant: PvpResultXpBreakdownParticipant }> = ({
  participant,
}) => {
  const { t } = useSafeTranslation();
  const [expanded, setExpanded] = useState(false);
  const breakdownPanelId = useId();

  return (
    <div className="xp-earned-section xp-earned-section--pvp-summary text-start mt-1">
      <button
        type="button"
        className="xp-earned-badge xp-earned-toggle justify-content-center w-100"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls={breakdownPanelId}
      >
        <i className="bi bi-star-fill text-warning me-2" aria-hidden />
        <span className="xp-earned-text">
          {t('quiz.xpEarned', '+{{xp}} XP Earned!').replace('{{xp}}', String(participant.earnedXP))}
        </span>
        <i
          className={`bi ms-1 ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}
          aria-hidden
        />
      </button>
      <Collapse in={expanded}>
        <div id={breakdownPanelId} className="xp-breakdown mt-2" role="region">
        <small className="text-muted d-block text-center">
          {t('quiz.baseXP', 'Base XP: +{{base}}').replace('{{base}}', String(participant.baseXp))} |{' '}
          {t('quiz.bonusXP', 'Bonus XP: +{{bonus}} ({{correct}} correct × {{perCorrect}} per correct)')
            .replace('{{bonus}}', String(participant.totalBonusXp))
            .replace('{{correct}}', String(participant.correctAnswers))
            .replace('{{perCorrect}}', String(participant.bonusXpPerCorrectAnswer))}
        </small>
        {participant.speedBonusXp != null && (
          <div className="mt-1">
            <small className="text-info">
              <i className="bi bi-lightning-charge-fill me-1" aria-hidden />
              {t('quiz.speedBonusXP', 'Speed bonus: +{{xp}} XP').replace(
                '{{xp}}',
                String(participant.speedBonusXp)
              )}
            </small>
          </div>
        )}
        {(participant.soloXpPotionBonusAmount ?? 0) > 0 && (
          <XpPotionBonusResultLine
            bonusAmount={participant.soloXpPotionBonusAmount ?? 0}
            bonusPercent={participant.soloXpPotionBonusPercent ?? 0}
          />
        )}
        {participant.soloVipXpBonusAmount > 0 && (
          <div className="mt-1 text-center">
            <small className="d-inline-flex align-items-center flex-wrap gap-1 vip-subscriber-nickname justify-content-center">
              <i className="bi bi-gem text-warning flex-shrink-0" title="VIP" aria-hidden />
              <span>
                {t('quiz.vipXpBonusXP', 'VIP bonus: +{{xp}} XP').replace(
                  '{{xp}}',
                  String(participant.soloVipXpBonusAmount)
                )}
              </span>
            </small>
          </div>
        )}
        {participant.pvpArenaExtraXp > 0 && (
          <div className="mt-1 text-center">
            <small className="text-primary">
              <i className="bi bi-lightning-fill me-1" aria-hidden />
              {t('pvp.arenaXpBonusLine', 'Arena PvP: +{{xp}} XP').replace(
                '{{xp}}',
                String(participant.pvpArenaExtraXp)
              )}
            </small>
          </div>
        )}
        {(participant.pvpAppXpBonusAmount ?? 0) > 0 && (
          <Alert
            variant="success"
            className="mt-2 mb-0 py-2 text-center quiz-results-app-xp-alert d-flex flex-column align-items-center"
          >
            <div className="fw-semibold d-flex justify-content-center align-items-center flex-wrap gap-1 small">
              <i className="bi bi-download" aria-hidden />
              <span>
                {t('quiz.appXpBonusHeadline', 'App XP bonus +{{percent}}% · +{{amount}} XP')
                  .replace('{{percent}}', String(participant.pvpAppXpBonusPercent ?? 15))
                  .replace('{{amount}}', String(participant.pvpAppXpBonusAmount ?? 0))}
              </span>
            </div>
            <small className="d-block mt-1 text-success-emphasis text-center w-100">
              {t('quiz.appXpBonusSubline', 'Extra XP for playing in the installed app.')}
            </small>
          </Alert>
        )}
        {participant.winnerBonusXp > 0 && (
          <div className="mt-1 text-center">
            <small className="text-success fw-semibold">
              <i className="bi bi-trophy me-1" aria-hidden />
              {t('pvp.winnerXpBonus', 'Winner bonus: +{{xp}} XP').replace(
                '{{xp}}',
                String(participant.winnerBonusXp)
              )}
            </small>
          </div>
        )}
        <small className="text-muted d-block text-center mt-2">
          {t('quiz.xpCalculated', 'XP calculated by server based on difficulty and performance')}
        </small>
        </div>
      </Collapse>
    </div>
  );
};

export default PvpResultParticipantXpBlock;
